import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api.js";
import { useVisiblePoll } from "../lib/useVisiblePoll.js";
import { WATCHLIST_POLL_MS, FLASH_DURATION_MS } from "../lib/pollConfig.js";

// Main page — sector tabs, filter pills (TECH only), watchlist + sector movers.
// Pasted verbatim from main-page-mockup.jsx (312 lines, approved v2 tag model)
// with these targeted swaps:
//   - INITIAL sample stocks     → api.watchlist.list(tabId) on mount+tab-change,
//                                 then api.quotes(symbols) merged in
//   - hardcoded movers array    → api.movers(tabId) on tab-change
//   - narrow useEffect listener → Tailwind xs: breakpoint (430px, matches
//                                 mockup's < 430 threshold in the config)
//   - onOpen(symbol) prop       → threaded into each watchlist row's › tap
//   - toggleStockTag / add /
//     removeStock                → api.watchlist.setTags / add / remove
//   - "◆ STOCKTWEAKS" wordmark  → <img src="/stocktweaks-logo.png" /> (28px)

const C = {
  bg: "#0A1428",
  panel: "#132038",
  border: "#1F3355",
  divider: "#16273F",
  chip: "#0E1A30",
  track: "#1B2C48",
  text: "#D5DEE9",
  mute: "#8194AE",
  dim: "#54709A",
  green: "#26D07C",
  red: "#FF5C5C",
  amber: "#E8B339",
};

// Display names → API tab ids. Mockup keeps the display-cased array to avoid
// visual drift; the id lookup lives here so the wire never uses casing hacks.
const TABS = ["TECH", "ETF", "Energy", "Bio", "Financials"];
const TAB_ID = { TECH: "tech", ETF: "etf", Energy: "energy", Bio: "bio", Financials: "financials" };
// QQQ and SPY removed — those are now their own tickers on the ETF tab, not filter pills.
const TECH_FILTERS = ["All", "AI-Infra", "FAANG", "CRYPTO"];

// tiny auto-tag suggester (client-side heuristic — the /profile endpoint is
// available if you want to grow this into a sector-driven suggestion later).
const SUGGEST = { NVDA: "AI-Infra", SMCI: "AI-Infra", GOOGL: "FAANG", AAPL: "FAANG", AMZN: "FAANG", MSTR: "CRYPTO", MARA: "CRYPTO", RIOT: "CRYPTO" };
// Kept in sync with TECH_FILTERS (minus "All" which isn't a real tag).
// Tags only exist on TECH — ETF/Energy/Bio/Financials are flat lists.
const TAG_OPTIONS = ["AI-Infra", "FAANG", "CRYPTO"];

function leanColor(s) {
  if (s <= -20) return C.red;
  if (s < 20) return C.amber;
  return C.green;
}
function leanWord(s) {
  if (s <= -60) return "STR SELL";
  if (s <= -20) return "SELL";
  if (s < 20) return "CONSOL";
  if (s < 60) return "BUY";
  return "STR BUY";
}

/* MiniLean — mockup used a `narrow` prop to swap width 40 ↔ 54. Now driven
   purely by Tailwind's xs: breakpoint on the outer bar so no JS listener
   is needed. The inline gradient background stays as-is (Tailwind arbitrary
   backgrounds don't work well for multi-stop gradients like this one). */
function MiniLean({ score }) {
  const pct = (score + 100) / 2;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      <div
        className="w-10 xs:w-[54px]"
        style={{ position: "relative", height: 6, borderRadius: 999, overflow: "hidden", background: "linear-gradient(90deg,#FF5C5C 0%,#E8B339 50%,#26D07C 100%)" }}
      >
        <div style={{ position: "absolute", top: 0, bottom: 0, left: `calc(${pct}% - 1.5px)`, width: 3, background: "#fff" }} />
      </div>
      <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 0.3, color: leanColor(score) }}>{leanWord(score)}</span>
    </div>
  );
}

export default function MainPage({ onOpen, user, onLogout }) {
  const [tab, setTab] = useState("TECH");
  const [filter, setFilter] = useState("All");
  const [stocks, setStocks] = useState([]);
  const [movers, setMovers] = useState([]);
  const [moversLoaded, setMoversLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  // Symbols that just had a price change — used to fire the flash CSS.
  // Cleared FLASH_DURATION_MS after the change lands.
  const [flashing, setFlashing] = useState(() => new Set());
  // Timeout handles for pending flash-clears; canceled on tab-change / unmount.
  const flashTimeouts = useRef(new Map());

  // add flow
  const [adding, setAdding] = useState(false);
  const [ticker, setTicker] = useState("");
  const [chosenTags, setChosenTags] = useState([]);
  const [editing, setEditing] = useState(null); // symbol being tag-edited

  const tabId = TAB_ID[tab];

  /* --- Load watchlist + movers on tab change ---
     Two requests total:
       1. /watchlist/full  → all rows in ONE response (sym+name+price+chg+lean+tags)
       2. /movers          → sector movers panel

     Previously this was 4 fan-out requests per tab (list + quotes + profiles +
     leans), each triggering per-symbol vendor calls server-side — ~24 concurrent
     Finnhub /quote calls per page load at N=8 rows, which regularly tripped
     Finnhub's per-second window and left ~7 of 8 rows permanently on "loading".

     /watchlist/full server-side fans out per-symbol with concurrency=3, caches
     each quote for 30s in KV, reuses that same cache for the lean compute, and
     retries with backoff on 429 — so refresh within 30s makes ZERO vendor calls,
     and even a cold load never bursts past Finnhub's rate limit. */
  useEffect(() => {
    let cancelled = false;
    setLoadErr(null);

    api.watchlist.full(tabId).then(({ stocks: rows }) => {
      if (!cancelled) setStocks(rows);
    }).catch(e => { if (!cancelled) setLoadErr(e.message); });

    setMoversLoaded(false);
    setMovers([]);
    api.movers(tabId).then(({ movers: m }) => {
      if (cancelled) return;
      setMovers(m || []);
      setMoversLoaded(true);
    }).catch(() => { if (!cancelled) { setMovers([]); setMoversLoaded(true); } });

    return () => { cancelled = true; };
  }, [tabId]);

  /* --- Auto-refresh watchlist while tab is visible ---
     Polls /watchlist/full every WATCHLIST_POLL_MS. The useVisiblePoll
     hook pauses when document.hidden, so background tabs don't burn
     vendor quota, and immediately re-fires when visibility returns.

     Merge preserves stable row fields (id, tags currently on client)
     and takes the server's fresh price/chg/lean/dayHigh/dayLow. When
     price actually changes for a symbol, we add it to `flashing` for
     a brief CSS background pulse.

     Backend outer wlfull cache is 25s so polls actually see fresh
     data instead of the same cached snapshot; per-symbol quote cache
     is 60s so most polls' fan-out costs one D1 read + KV reads. */
  const pollWatchlist = useCallback(() => {
    if (!tabId) return;
    api.watchlist.full(tabId).then(({ stocks: fresh }) => {
      const bySym = new Map(fresh.map(s => [s.sym, s]));
      const changed = [];
      setStocks(cur => cur.map(old => {
        const f = bySym.get(old.sym);
        if (!f) return old;
        const priceChanged =
          Number.isFinite(f.price) &&
          Number.isFinite(old.price) &&
          Math.abs(f.price - old.price) > 0.001;
        if (priceChanged) changed.push(old.sym);
        return {
          ...old,
          price:     f.price,
          chg:       f.chg,
          lean:      f.lean,
          dayHigh:   f.dayHigh,
          dayLow:    f.dayLow,
          bad_quote: f.bad_quote,
          name:      f.name || old.name,
        };
      }));
      if (changed.length) {
        setFlashing(prev => {
          const next = new Set(prev);
          for (const s of changed) next.add(s);
          return next;
        });
        for (const sym of changed) {
          // Cancel any prior timer for this symbol before scheduling a new one.
          const prior = flashTimeouts.current.get(sym);
          if (prior) clearTimeout(prior);
          const t = setTimeout(() => {
            setFlashing(prev => {
              if (!prev.has(sym)) return prev;
              const next = new Set(prev);
              next.delete(sym);
              return next;
            });
            flashTimeouts.current.delete(sym);
          }, FLASH_DURATION_MS);
          flashTimeouts.current.set(sym, t);
        }
      }
    }).catch(() => { /* swallow — next tick will retry */ });
  }, [tabId]);

  useVisiblePoll(pollWatchlist, WATCHLIST_POLL_MS);

  // Clean up any in-flight flash timers on tab change / unmount so they
  // don't fire against a stale symbol list.
  useEffect(() => () => {
    for (const t of flashTimeouts.current.values()) clearTimeout(t);
    flashTimeouts.current.clear();
  }, []);

  const isTech = tab === "TECH";
  const suggestion = SUGGEST[ticker.trim().toUpperCase()] || null;

  // Filters are driven ONLY by the tags manually assigned via the ✎ editor.
  // There is NO auto-classification by sector / industry / vendor metadata
  // — the /watchlist/full API returns exactly the rows in `stock_tags` for
  // that user. So:
  //   - "All" shows every row
  //   - a specific filter shows ONLY rows whose `tags` array contains that
  //     exact tag string
  //   - untagged stocks (tags=[]) appear ONLY under "All"
  //
  // Defensive: guard against a possibly-undefined tags field + do a
  // case-insensitive comparison (both server + client use uppercase-cased
  // tag names, but explicit normalization prevents future casing drift).
  const visible = isTech
    ? filter === "All"
      ? stocks
      : stocks.filter((s) => {
          if (!Array.isArray(s.tags) || s.tags.length === 0) return false;
          const target = String(filter).toLowerCase();
          return s.tags.some((t) => String(t).toLowerCase() === target);
        })
    : stocks;

  const toggleTag = (t) =>
    setChosenTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const confirmAdd = async () => {
    const sym = ticker.trim().toUpperCase();
    if (!sym) return;
    // Optimistic UI: append immediately so the watchlist reflects the add.
    setStocks((cur) => [
      ...cur,
      { id: `pending-${sym}`, sym, name: "—", price: 0, chg: 0, lean: 0, tags: chosenTags },
    ]);
    setTicker("");
    setChosenTags([]);
    setAdding(false);
    setFilter("All");
    try {
      const res = await api.watchlist.add({ sym, tab: tabId, tags: chosenTags });
      // Replace the pending row with the real id from the server.
      setStocks((cur) => cur.map(s => s.sym === sym && String(s.id).startsWith("pending-") ? { ...s, id: res.id } : s));
      // Enrich the new row with a live quote
      api.quotes([sym]).then(({ quotes }) => {
        const q = quotes[0]; if (!q) return;
        setStocks(cur => cur.map(r => r.sym === sym ? { ...r, price: q.price, chg: q.chg } : r));
      }).catch(() => {});
    } catch { /* keep the optimistic row so the user can retry */ }
  };

  const toggleStockTag = (sym, t) => {
    // Optimistic — mockup expects immediate visual toggle
    setStocks((cur) => {
      const next = cur.map((s) =>
        s.sym === sym
          ? { ...s, tags: s.tags.includes(t) ? s.tags.filter((x) => x !== t) : [...s.tags, t] }
          : s
      );
      // Push to server after local state settled
      const updated = next.find(s => s.sym === sym);
      if (updated && Number.isFinite(updated.id)) {
        api.watchlist.setTags(updated.id, updated.tags).catch(() => {});
      }
      return next;
    });
  };

  const inWatchlist = (sym) => stocks.some((s) => s.sym === sym);
  const addFromMover = async (m) => {
    if (inWatchlist(m.sym)) return;
    // Optimistic append
    const tempId = `pending-${m.sym}`;
    setStocks((cur) => [...cur, { id: tempId, sym: m.sym, name: "—", price: 0, chg: m.chg, lean: 0, tags: [] }]);
    try {
      const res = await api.watchlist.add({ sym: m.sym, tab: tabId, tags: [] });
      setStocks((cur) => cur.map(s => s.id === tempId ? { ...s, id: res.id } : s));
    } catch { /* leave optimistic row */ }
  };
  const removeStock = async (sym) => {
    const row = stocks.find(s => s.sym === sym);
    setStocks((cur) => cur.filter((s) => s.sym !== sym));
    setEditing(null);
    if (row && Number.isFinite(row.id)) {
      try { await api.watchlist.remove(row.id); } catch {}
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", overflowX: "hidden", boxSizing: "border-box" }}>
      {/* No outer flex — `mx-auto` centers the block directly. Padding
          moves onto the inner div and scales with the breakpoint so the
          content doesn't hug the screen edge on wide displays.
          Widths targeted at the actual devices:
            default (mobile): 480px cap, matches mockup
            xs (430+):        matches Z Fold FOLDED and small phones
            md (768+):        typical laptop viewport
            lg (1024+):       typical desktop
            xl (1280+):       Z Fold UNFOLDED (~830 CSS px is above md;
                              want more comfortable width by lg)
            2xl (1536+):      wide desktop
          */}
      <div
        className="mx-auto w-full max-w-[480px] sm:max-w-[620px] md:max-w-[820px] lg:max-w-[1080px] xl:max-w-[1240px] 2xl:max-w-[1400px] px-3 sm:px-4 md:px-6 lg:px-10"
        style={{ paddingBottom: 28, boxSizing: "border-box" }}
      >
        {/* Sticky top */}
        <div style={{ position: "sticky", top: 0, zIndex: 10, background: C.bg, paddingTop: 12, borderBottom: `1px solid ${C.border}`, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8, minWidth: 0 }}>
            {/* Wordmark replaces the mockup's "◆ STOCKTWEAKS" text.
                Height scales FLUIDLY with viewport — same continuous curve
                as the fonts (see lib/useFontScale.js). Uses CSS clamp() so
                it grows smoothly across all screen widths instead of
                jumping at breakpoints.
                  clamp(38px, 4.5vw + 20px, 64px)
                  → 375px viewport: 38px (readable on phone)
                  → 720px:          52.4px
                  → 830px (Fold):   57.4px
                  → 1024px:         64px  (capped)
                Bigger than the earlier 28px mobile anchor so it doesn't
                get lost in the header. */}
            <img
              src="/stocktweaks-logo.png"
              alt="StockTweaks"
              style={{
                height:     "clamp(38px, 4.5vw + 20px, 64px)",
                width:      "auto",
                display:    "block",
                flexShrink: 0,
              }}
            />
            {/* Signed-in greeting + logout replaces the mockup's "market open"
                chip. Greeting reads users.name (per-user) from /auth/whoami,
                falls back to the email prefix if name is null so a user who
                hasn't set a display name still sees a personal-looking
                greeting instead of a broken "Hi ". `title` still shows the
                full email on hover — useful when a shared-machine user
                needs to confirm which account is logged in. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span
                className="hidden xs:inline"
                style={{ fontSize: 15, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220, letterSpacing: 0.2 }}
                title={user && user.email}
              >
                {user && `Hi ${(user.name && user.name.trim().split(/\s+/)[0]) || (user.email && user.email.split("@")[0]) || "there"}`}
              </span>
              <button
                onClick={onLogout}
                style={{ fontSize: 10, color: C.dim, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
              >
                sign out
              </button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, overflowX: "auto" }}>
            {TABS.map((t) => {
              const active = t === tab;
              return (
                <button key={t} onClick={() => { setTab(t); setFilter("All"); setAdding(false); }} style={{ flexShrink: 0, padding: "8px 14px", fontSize: 13, fontWeight: active ? 700 : 500, fontFamily: "inherit", border: "none", cursor: "pointer", background: "transparent", color: active ? C.text : C.dim, borderBottom: active ? `2px solid ${C.amber}` : "2px solid transparent" }}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Filter pills */}
        {isTech && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, marginBottom: 12 }}>
            {TECH_FILTERS.map((f) => {
              const active = f === filter;
              return (
                <button key={f} onClick={() => setFilter(f)} style={{ flexShrink: 0, padding: "5px 12px", fontSize: 12, fontWeight: active ? 700 : 500, fontFamily: "inherit", borderRadius: 999, cursor: "pointer", border: `1px solid ${active ? C.amber : C.border}`, background: active ? C.amber : "transparent", color: active ? C.bg : C.mute }}>
                  {f}
                </button>
              );
            })}
          </div>
        )}

        {/* Watchlist */}
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.panel, overflow: "hidden", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: C.mute }}>
              {isTech ? filter : tab} watchlist
            </span>
            <button onClick={() => setAdding((a) => !a)} style={{ fontSize: 11, fontFamily: "inherit", cursor: "pointer", border: `1px solid ${C.border}`, borderRadius: 6, background: adding ? C.chip : "transparent", color: adding ? C.amber : C.mute, padding: "3px 10px" }}>
              {adding ? "× cancel" : "+ add"}
            </button>
          </div>

          {/* Add flow */}
          {adding && (
            <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}`, background: C.chip }}>
              <input
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="ticker, e.g. NVDA"
                style={{ width: "100%", boxSizing: "border-box", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 10px", color: C.text, fontFamily: "inherit", fontSize: 14, outline: "none", marginBottom: 10 }}
              />
              {/* Tag chips + suggestion hint are TECH-only — the other tabs are
                  flat lists with no tagging concept. On ETF/Energy/Bio/Financials
                  the add flow is just [ticker] + [Add]. */}
              {isTech && (
                <>
                  <div style={{ fontSize: 10, color: C.mute, marginBottom: 6 }}>
                    tap tags to file it — or add none, it lives under All
                    {suggestion && <span style={{ color: C.amber }}>  · suggested: {suggestion}</span>}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                    {TAG_OPTIONS.map((t) => {
                      const on = chosenTags.includes(t);
                      const isSug = suggestion === t && !on;
                      return (
                        <button key={t} onClick={() => toggleTag(t)} style={{ fontSize: 12, fontFamily: "inherit", cursor: "pointer", borderRadius: 999, padding: "5px 12px", border: `1px solid ${on ? C.green : isSug ? C.amber : C.border}`, background: on ? C.green : "transparent", color: on ? C.bg : isSug ? C.amber : C.mute, fontWeight: on ? 700 : 500 }}>
                          {on ? "✓ " : isSug ? "＋ " : ""}
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              <button onClick={confirmAdd} disabled={!ticker.trim()} style={{ width: "100%", fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: ticker.trim() ? "pointer" : "default", border: "none", borderRadius: 6, padding: "9px", background: ticker.trim() ? C.amber : C.border, color: ticker.trim() ? C.bg : C.dim }}>
                Add {ticker.trim() ? ticker.trim() : "stock"}
                {isTech && (chosenTags.length ? ` → ${chosenTags.join(", ")}` : " → untagged (All)")}
              </button>
            </div>
          )}

          {visible.map((r, i) => {
            const up = r.chg >= 0;
            const col = up ? C.green : C.red;
            const hasData = r.price > 0;
            // Flash background pulse when this row's price just changed
            // via the auto-poll. CSS transition fades from tinted → transparent
            // over FLASH_DURATION_MS. Green for up-moves, red for down-moves.
            const isFlashing = flashing.has(r.sym);
            const flashBg = isFlashing
              ? (up ? "rgba(38, 208, 124, 0.16)" : "rgba(255, 92, 92, 0.16)")
              : "transparent";
            // Lighter blue for the secondary row text + pencil — was C.dim (#54709A,
            // ~3.2:1 contrast on the panel bg, borderline AA). #A6BAD1 gives
            // ~8:1 which is comfortably readable on a phone in daylight. The
            // ticker (r.sym) stays C.text because it's already bright.
            const rowSecondary = "#A6BAD1";
            return (
              <div key={r.sym} style={{ borderBottom: i === visible.length - 1 ? "none" : `1px solid ${C.divider}` }}>
                {/* Outer row flex owns the navigate-to-detail tap. The pencil
                    button inside stops propagation so the tag editor only opens
                    when the pencil itself is tapped. Prior behavior — tap
                    anywhere = tag editor — was wrong: the primary action from
                    the watchlist row is "go read about this stock", not "edit
                    tags"; tag editing is a rarer housekeeping task. */}
                <div
                  onClick={() => onOpen && onOpen(r.sym)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 14px", background: flashBg, transition: `background ${FLASH_DURATION_MS}ms ease-out`, cursor: "pointer" }}
                >
                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: "1 1 auto" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{r.sym}</span>
                      {/* Tag pills + "untagged" state are TECH-only. On the flat
                          list tabs (ETF/Energy/Bio/Financials) the tag concept
                          doesn't exist, so we hide those bits. The ✎ pencil
                          still opens an editor for those tabs, but it only
                          shows the Remove button — no tag chips. */}
                      {isTech && r.tags.length === 0 && (
                        <span style={{ fontSize: 8, color: rowSecondary, border: `1px solid ${C.border}`, borderRadius: 4, padding: "0 4px" }}>untagged</span>
                      )}
                      {isTech && r.tags.map((t) => (
                        <span key={t} style={{ fontSize: 8, color: C.amber, border: `1px solid ${C.amber}`, borderRadius: 4, padding: "0 4px" }}>{t}</span>
                      ))}
                      {/* Pencil — its OWN tap target. e.stopPropagation() so
                          the outer navigate handler doesn't also fire. The
                          padding around the glyph gives ~28x24px of hit area
                          (vs. the old bare 9px span at ~10x12px) so it's
                          reliably tappable on mobile without overlapping the
                          row's navigate tap. aria-label for screen readers. */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditing(editing === r.sym ? null : r.sym);
                        }}
                        aria-label={`Edit tags for ${r.sym}`}
                        style={{
                          fontSize:     13,
                          lineHeight:   1,
                          color:        editing === r.sym ? C.amber : rowSecondary,
                          background:   "transparent",
                          border:       "none",
                          padding:      "4px 8px",
                          margin:       "-4px -4px -4px 0",   // negate padding for tight visual layout
                          cursor:       "pointer",
                          fontFamily:   "inherit",
                        }}
                      >✎</button>
                    </div>
                    {/* Company name — hidden below xs (mockup's narrow behavior) */}
                    <span className="hidden xs:inline" style={{ fontSize: 10, color: rowSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, fontVariantNumeric: "tabular-nums", minWidth: 62 }}>
                    <span style={{ fontSize: 13, color: hasData ? C.text : C.dim }}>{hasData ? r.price.toFixed(2) : "—"}</span>
                    <span style={{ fontSize: 11, color: hasData ? col : C.dim }}>{hasData ? `${up ? "+" : ""}${r.chg.toFixed(1)}%` : "loading"}</span>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {hasData
                      ? <MiniLean score={r.lean} />
                      : <span className="w-10 xs:w-[54px]" style={{ fontSize: 9, color: C.dim, display: "inline-block", textAlign: "center" }}>—</span>}
                  </div>
                  {/* › — now purely visual; the parent flex handles the
                      navigate tap so users get the whole row as a target. */}
                  <span style={{ color: C.dim, fontSize: 16, flexShrink: 0 }}>›</span>
                </div>

                {/* inline tag editor */}
                {editing === r.sym && (
                  <div style={{ padding: "0 14px 12px 14px", background: C.chip }}>
                    <div style={{ fontSize: 10, color: C.mute, padding: "8px 0 8px 0" }}>
                      {isTech ? `tap to assign / remove tags for ${r.sym}` : `remove ${r.sym} from watchlist?`}
                    </div>
                    {isTech && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {TAG_OPTIONS.map((t) => {
                          const on = r.tags.includes(t);
                          return (
                            <button key={t} onClick={() => toggleStockTag(r.sym, t)} style={{ fontSize: 12, fontFamily: "inherit", cursor: "pointer", borderRadius: 999, padding: "5px 12px", border: `1px solid ${on ? C.green : C.border}`, background: on ? C.green : "transparent", color: on ? C.bg : C.mute, fontWeight: on ? 700 : 500 }}>
                              {on ? "✓ " : "+ "}
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button onClick={() => removeStock(r.sym)} style={{ flex: "0 0 auto", fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", border: `1px solid ${C.red}`, borderRadius: 6, padding: "8px 14px", background: "transparent", color: C.red }}>
                        🗑 Remove
                      </button>
                      <button onClick={() => setEditing(null)} style={{ flex: "1 1 auto", fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", border: "none", borderRadius: 6, padding: "8px", background: C.amber, color: C.bg }}>
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {visible.length === 0 && !loadErr && (
            <div style={{ padding: "20px 14px", textAlign: "center", fontSize: 12, color: C.dim }}>
              No {filter} names yet. Add one, or tag an existing stock.
            </div>
          )}
          {loadErr && (
            <div style={{ padding: "20px 14px", textAlign: "center", fontSize: 11, color: C.red }}>
              Couldn't load watchlist — {loadErr}
            </div>
          )}
        </div>

        {/* Sector movers — live from /movers */}
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.panel, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: C.amber }}>◆ {tab} movers</span>
            <span style={{ fontSize: 10, color: C.dim }}>biggest % · now</span>
          </div>
          <div>
            {movers.slice(0, 6).map((m, i, arr) => {
              const up = m.chg >= 0;
              const onList = inWatchlist(m.sym);
              return (
                <div key={m.sym} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "11px 14px", borderBottom: i === arr.length - 1 ? "none" : `1px solid ${C.divider}` }}>
                  <span style={{ width: 64, fontSize: 14, fontWeight: 700, color: C.text, flexShrink: 0 }}>{m.sym}</span>
                  <span style={{ flex: "1 1 auto", fontSize: 13, color: up ? C.green : C.red, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {up ? "▲ +" : "▼ "}
                    {m.chg.toFixed(1)}%
                  </span>
                  {onList ? (
                    <span title="on your watchlist" style={{ fontSize: 16, color: C.green, fontWeight: 700, flexShrink: 0, width: 34, textAlign: "center" }}>
                      ✓
                    </span>
                  ) : (
                    <button onClick={() => addFromMover(m)} title="add to watchlist" style={{ fontSize: 18, lineHeight: 1, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", flexShrink: 0, borderRadius: 6, border: `1px solid ${C.amber}`, background: "transparent", color: C.amber, padding: "4px 0", width: 34 }}>
                      +
                    </button>
                  )}
                </div>
              );
            })}
            {movers.length === 0 && (
              <div style={{ padding: "16px 14px", textAlign: "center", fontSize: 11, color: C.dim }}>
                {moversLoaded ? `No ${tab} movers today above the price/mcap floors` : "Loading movers…"}
              </div>
            )}
          </div>
          <div style={{ fontSize: 9, color: C.dim, padding: "8px 14px" }}>whole-sector movers — surfaces names not on your watchlist</div>
        </div>

        <div style={{ textAlign: "center", fontSize: 9, color: C.dim, marginTop: 14 }}>Live data · not investment advice</div>
      </div>
    </div>
  );
}
